import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import ColumnFilter, { FilterConfig } from './ColumnFilter';
import ReportFilterBar from './ReportFilterBar';
import { FilterFieldMetadata, hasAtLeastOneFilter, optionsFromValues } from '../utils/filterUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface InventoryTransaction {
  itemName: string;
  quantityUnit: string;
  quantity: number;
  transactionType: string;
  createdDate: string;
  notes: string | null;
  shopName: string | null;
  username: string;
}

type Order = 'asc' | 'desc';

// Utility function to convert UTC date string to browser's local timezone
const convertUTCToLocalTime = (utcDateString: string): string => {
  try {
    // The server is in UTC timezone, so database dates are in UTC
    // Format: "yyyy-MM-dd HH:mm:ss" (UTC time)
    let dateString = utcDateString;
    
    // Convert space to 'T' and add 'Z' to make it explicit UTC
    if (dateString.includes(' ')) {
      dateString = dateString.replace(' ', 'T') + 'Z';
    } else if (dateString.includes('T') && !dateString.includes('Z')) {
      dateString = dateString + 'Z';
    }
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', utcDateString);
      return utcDateString;
    }
    
    // Return formatted date in local timezone
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error converting date:', utcDateString, error);
    return utcDateString;
  }
};

const InventoryReport: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [inventoryTransactions, setInventoryTransactions] = useState<InventoryTransaction[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterMetadata, setFilterMetadata] = useState<Record<string, FilterFieldMetadata> | null>(null);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof InventoryTransaction>('createdDate');
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});

  useEffect(() => {
    if (token) {
      fetchFilterMetadata();
    }
  }, [token]);

  const fetchFilterMetadata = async () => {
    setIsLoadingMetadata(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory-transactions/filter-metadata`, {
        headers: { Authorization: token }
      });
      setFilterMetadata(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching inventory filter metadata:', err);
      }
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const buildInventoryQueryParams = () => {
    const params: Record<string, any> = {};
    if (columnFilters.itemName) {
      params.itemName = columnFilters.itemName;
    }
    if (columnFilters.quantity?.min !== undefined && columnFilters.quantity?.min !== '') {
      params.quantityMin = columnFilters.quantity.min;
    }
    if (columnFilters.quantity?.max !== undefined && columnFilters.quantity?.max !== '') {
      params.quantityMax = columnFilters.quantity.max;
    }
    if (columnFilters.quantityUnit) {
      params.quantityUnit = columnFilters.quantityUnit;
    }
    if (columnFilters.transactionType) {
      params.transactionType = columnFilters.transactionType;
    }
    if (columnFilters.createdDate?.start && columnFilters.createdDate?.end) {
      params.start = `${columnFilters.createdDate.start} 00:00:00`;
      params.end = `${columnFilters.createdDate.end} 23:59:59`;
    }
    if (columnFilters.shopName) {
      params.shopName = columnFilters.shopName;
    }
    if (columnFilters.username) {
      params.username = columnFilters.username;
    }
    if (columnFilters.notes) {
      params.notes = columnFilters.notes;
    }
    return params;
  };

  const fetchInventoryTransactions = async () => {
    setIsLoadingTransactions(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory-transactions`, {
        headers: { Authorization: token },
        params: buildInventoryQueryParams()
      });
      setInventoryTransactions(response.data);
      setHasSearched(true);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching inventory transactions:', err);
      }
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  const handleSearch = () => {
    fetchInventoryTransactions();
  };

  const handleClearFilters = () => {
    setColumnFilters({});
    setInventoryTransactions([]);
    setHasSearched(false);
  };

  const getComparator = <Key extends keyof InventoryTransaction>(
    order: Order,
    orderBy: Key,
  ): (a: InventoryTransaction, b: InventoryTransaction) => number => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = <T extends unknown>(a: T, b: T, orderBy: keyof T) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const stableSort = <T extends unknown>(array: T[], comparator: (a: T, b: T) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };


  const getFilteredTransactions = () => {
    const filtered = [...inventoryTransactions];

    return filtered.sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleRequestSort = (property: keyof InventoryTransaction) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const columnFilterConfigs: FilterConfig[] = [
    {
      field: 'itemName',
      type: 'select',
      label: 'Item Name',
      placeholder: 'Filter by item name',
      autocomplete: true,
      options: optionsFromValues(filterMetadata?.itemName?.values)
    },
    {
      field: 'quantity',
      type: 'number',
      label: 'Quantity',
      placeholder: 'Filter by quantity',
      range: true,
      min: filterMetadata?.quantity?.min,
      max: filterMetadata?.quantity?.max
    },
    {
      field: 'quantityUnit',
      type: 'select',
      label: 'Unit',
      placeholder: 'Filter by unit',
      options: optionsFromValues(filterMetadata?.quantityUnit?.values)
    },
    {
      field: 'transactionType',
      type: 'select',
      label: 'Transaction Type',
      options: optionsFromValues(filterMetadata?.transactionType?.values)
    },
    {
      field: 'createdDate',
      type: 'date',
      label: 'Created Date',
      min: filterMetadata?.createdDate?.min,
      max: filterMetadata?.createdDate?.max
    },
    {
      field: 'shopName',
      type: 'select',
      label: 'Shop',
      placeholder: 'Filter by shop',
      autocomplete: true,
      options: optionsFromValues(filterMetadata?.shopName?.values)
    },
    {
      field: 'username',
      type: 'select',
      label: 'User',
      placeholder: 'Filter by user',
      autocomplete: true,
      options: optionsFromValues(filterMetadata?.username?.values)
    },
    {
      field: 'notes',
      type: 'string',
      label: 'Notes',
      placeholder: 'Filter by notes'
    }
  ];

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  // Sum of quantity for filtered/selected rows (for total row display)
  const getQuantitySumForFilteredRows = () => {
    const filtered = getFilteredTransactions();
    return filtered.reduce((sum, t) => sum + (t.quantity || 0), 0);
  };

  // PDF export function
  const exportInventoryReportToPDF = () => {
    try {
      const doc = new jsPDF('landscape');
      const filteredData = getFilteredTransactions();
      const currentDate = new Date().toLocaleString();

      // Add title and metadata
      doc.setFontSize(16);
      doc.text('Inventory Transaction Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${currentDate}`, 14, 22);

      // Prepare table data
      const tableData = filteredData.map(transaction => [
        transaction.itemName,
        transaction.quantity.toString(),
        transaction.quantityUnit,
        transaction.transactionType,
        convertUTCToLocalTime(transaction.createdDate),
        transaction.shopName || '-',
        transaction.username,
        transaction.notes || '-'
      ]);

      // Prepare headers
      const headers = [
        'Item Name',
        'Quantity',
        'Unit',
        'Transaction Type',
        'Created Date',
        'Shop',
        'User',
        'Notes'
      ];

      // Add table to PDF
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 29,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] }
      });

      doc.save('inventory-transaction-report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ width: '100%' }}>
      {isLoadingMetadata ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <ReportFilterBar
          configs={columnFilterConfigs}
          values={columnFilters}
          onChange={handleColumnFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          searchDisabled={!hasAtLeastOneFilter(columnFilters)}
          isSearching={isLoadingTransactions}
          extraActions={
            <Button
              variant="outlined"
              onClick={exportInventoryReportToPDF}
              disabled={!hasSearched || inventoryTransactions.length === 0}
              fullWidth={isMobile}
            >
              Export PDF
            </Button>
          }
          resultCount={inventoryTransactions.length}
          hasSearched={hasSearched}
        />
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          position: 'relative',
          minHeight: { xs: 220, md: 180 },
          maxHeight: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 320px)' },
          '& .MuiTableCell-root': {
            padding: { xs: '8px 10px', md: '8px 16px' },
            fontSize: '0.875rem',
            whiteSpace: 'nowrap'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'itemName'}
                    direction={orderBy === 'itemName' ? order : 'asc'}
                    onClick={() => handleRequestSort('itemName')}
                  >
                    Item Name
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[0]}
                      value={columnFilters.itemName}
                      onChange={(value) => handleColumnFilterChange('itemName', value)}
                      onClear={() => handleColumnFilterClear('itemName')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={orderBy === 'quantity'}
                    direction={orderBy === 'quantity' ? order : 'asc'}
                    onClick={() => handleRequestSort('quantity')}
                  >
                    Quantity
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[1]}
                      value={columnFilters.quantity}
                      onChange={(value) => handleColumnFilterChange('quantity', value)}
                      onClear={() => handleColumnFilterClear('quantity')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'quantityUnit'}
                    direction={orderBy === 'quantityUnit' ? order : 'asc'}
                    onClick={() => handleRequestSort('quantityUnit')}
                  >
                    Unit
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[2]}
                      value={columnFilters.quantityUnit}
                      onChange={(value) => handleColumnFilterChange('quantityUnit', value)}
                      onClear={() => handleColumnFilterClear('quantityUnit')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'transactionType'}
                    direction={orderBy === 'transactionType' ? order : 'asc'}
                    onClick={() => handleRequestSort('transactionType')}
                  >
                    Transaction Type
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[3]}
                      value={columnFilters.transactionType}
                      onChange={(value) => handleColumnFilterChange('transactionType', value)}
                      onClear={() => handleColumnFilterClear('transactionType')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'createdDate'}
                    direction={orderBy === 'createdDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdDate')}
                  >
                    Created Date
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[4]}
                      value={columnFilters.createdDate}
                      onChange={(value) => handleColumnFilterChange('createdDate', value)}
                      onClear={() => handleColumnFilterClear('createdDate')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'shopName'}
                    direction={orderBy === 'shopName' ? order : 'asc'}
                    onClick={() => handleRequestSort('shopName')}
                  >
                    Shop
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[5]}
                      value={columnFilters.shopName}
                      onChange={(value) => handleColumnFilterChange('shopName', value)}
                      onClear={() => handleColumnFilterClear('shopName')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'username'}
                    direction={orderBy === 'username' ? order : 'asc'}
                    onClick={() => handleRequestSort('username')}
                  >
                    User
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[6]}
                      value={columnFilters.username}
                      onChange={(value) => handleColumnFilterChange('username', value)}
                      onClear={() => handleColumnFilterClear('username')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'notes'}
                    direction={orderBy === 'notes' ? order : 'asc'}
                    onClick={() => handleRequestSort('notes')}
                  >
                    Notes
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[7]}
                      value={columnFilters.notes}
                      onChange={(value) => handleColumnFilterChange('notes', value)}
                      onClear={() => handleColumnFilterClear('notes')}
                    />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingTransactions ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                      Loading results...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : getFilteredTransactions().length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {hasSearched ? 'No results found' : 'Select filters and click Search'}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {stableSort(getFilteredTransactions(), getComparator(order, orderBy))
                  .map((transaction, index) => (
                    <TableRow key={index}>
                      <TableCell>{transaction.itemName}</TableCell>
                      <TableCell align="right">{transaction.quantity}</TableCell>
                      <TableCell>{transaction.quantityUnit}</TableCell>
                      <TableCell>{transaction.transactionType}</TableCell>
                      <TableCell>{convertUTCToLocalTime(transaction.createdDate)}</TableCell>
                      <TableCell>{transaction.shopName || '-'}</TableCell>
                      <TableCell>{transaction.username}</TableCell>
                      <TableCell>{transaction.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                <TableRow
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 3,
                    backgroundColor: 'primary.main',
                    '& .MuiTableCell-root': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 'bold',
                      borderTop: '2px solid',
                      borderTopColor: 'primary.dark'
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                  <TableCell align="right">{getQuantitySumForFilteredRows()}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InventoryReport;
